import { useCallback, useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

// Module-scoped notification bus to ensure a single realtime channel per user
const notificationBus = {
  channel: null,
  userId: null,
  listeners: new Map(),
  nextId: 1,
};

/**
 * useNotifications - Fetches user notifications with real-time updates
 */
export const useNotifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cleanup;
    if (user?.id) {
      fetchNotifications();
      // register a listener with the shared bus
      cleanup = subscribeToNotifications((payload) => {
        if (['workshop', 'announcement'].includes(payload.new?.type)) {
          setNotifications(prev => [payload.new, ...prev]);
          if (!payload.new.is_read) {
            setUnreadCount(prev => prev + 1);
          }
        }
      });
    }

    return () => {
      if (typeof cleanup === 'function') cleanup();
    };
  }, [user?.id]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_archived', false)
        .in('type', ['workshop', 'announcement'])
        .order('created_at', { ascending: false })
        .limit(10);

      if (fetchError) throw fetchError;

      if (data && data.length > 0) {
        setNotifications(data);
        const unread = data.filter(n => !n.is_read).length;
        setUnreadCount(unread);
      } else {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Subscribe with a per-hook callback that is fanned out from a single channel
  const subscribeToNotifications = (callback) => {
    const id = notificationBus.nextId++;
    notificationBus.listeners.set(id, callback);

    // If there's no channel for this user, create it and attach a single handler
    if (!notificationBus.channel || notificationBus.userId !== user.id) {
      // clean up previous channel if user changed
      if (notificationBus.channel) {
        try {
          notificationBus.channel.unsubscribe();
        } catch (e) {
          // ignore
        }
        notificationBus.channel = null;
        notificationBus.userId = null;
        notificationBus.listeners.clear();
      }

      const channel = supabase
        .channel(`notifications:${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            // fan out to all registered listeners
            for (const cb of notificationBus.listeners.values()) {
              try {
                cb(payload);
              } catch (err) {
                console.error('notification callback error', err);
              }
            }
          }
        )
        .subscribe();

      notificationBus.channel = channel;
      notificationBus.userId = user.id;
    }

    // return unsubscribe for this listener
    return () => {
      notificationBus.listeners.delete(id);
      // if no listeners remain, remove the channel
      if (notificationBus.listeners.size === 0 && notificationBus.channel) {
        try {
          notificationBus.channel.unsubscribe();
        } catch (e) {
          // ignore
        }
        notificationBus.channel = null;
        notificationBus.userId = null;
      }
    };
  };

  const markAsRead = async (notificationId) => {
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const archiveNotification = async (notificationId) => {
    try {
      await supabase
        .from('notifications')
        .update({ is_archived: true })
        .eq('id', notificationId);

      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (err) {
      console.error('Error archiving notification:', err);
    }
  };

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    archiveNotification,
    refetch: fetchNotifications,
  };
};

export default useNotifications;
